import React, { useEffect, useState, useRef } from 'react';
import { PDP_SIDEBAR_MENU_CLICKED } from '@/constants/events';
import { bigDesktop, tablet } from '@/constants/screensizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import useAccount from '@/lib/hooks/useAccount';

interface SidebarSection {
  icon: any;
  name: string;
  value: string;
}

interface SidebarPDPProps {
  sidebarSections: SidebarSection[];
  isBundleAvailable: boolean;
}

const SCROLL_OFFSET = 150;
const CLICK_SCROLL_TIMEOUT = 800;

const SidebarPDP = ({ sidebarSections, isBundleAvailable }: SidebarPDPProps) => {
  const [activeSection, setActiveSection] = useState(0);
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  const isClickScrolling = useRef(false);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isMobile] = useMediaQuery(tablet);
  const [isBigDesktop] = useMediaQuery(bigDesktop);
  const [isServer, setIsServer] = useState(true);

  const { account } = useAccount();

  useEffect(() => {
    setIsServer(false);
  }, []);

  useEffect(() => {
    const el = itemRefs.current[activeSection];
    if (el) {
      setIndicatorStyle({ top: el.offsetTop, height: el.offsetHeight });
    }
  }, [activeSection, sidebarSections]);

  useEffect(() => {
    const handleScroll = () => {
      if (isClickScrolling.current) return; // 🔒 lock during click scroll

      sidebarSections.forEach((section, index) => {
        const el = document.getElementById(section.value);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const isInView =
          rect.top <= SCROLL_OFFSET && rect.bottom >= SCROLL_OFFSET;

        if (isInView) {
          setActiveSection(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sidebarSections]);

  const scrollToElement = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    window.scrollTo({
      top:
        rect.top +
        window.scrollY -
        (isBigDesktop ? 80 : 40) -
        115,
      behavior: 'smooth',
    });
  };

  const scrollToSection = (id: string) => {
    if (isServer) return;
    const el = document.getElementById(id);
    if (el) scrollToElement(el);
  };

  const handleClick = (index: number) => {
    const section = sidebarSections[index];

    isClickScrolling.current = true;

    setActiveSection(index);

    if (typeof window !== 'undefined') {
      window.dataLayer = (window.dataLayer as any) || [];
      window.dataLayer.push({
        event: PDP_SIDEBAR_MENU_CLICKED,
        section: section.name,
        userId: account?.accountId,
      });
    }

    scrollToSection(section.value);

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickTimeoutRef.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, CLICK_SCROLL_TIMEOUT);
  };

  return (
    <>
      {!isMobile ? (
        <div className="absolute left-full z-10 w-screen -translate-x-full px-24 lg:grid lg:grid-cols-12">
          <div className="w-full">
            <select
              className="w-full rounded-md border border-gray-300 px-4 py-2"
              value={activeSection}
              onChange={(e) => handleClick(Number(e.target.value))}
            >
              {sidebarSections.map((section, index) => (
                <option key={section.value} value={index}>
                  {section.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="absolute">
          <div className="sticky top-[50px] z-10 w-3/4">
            <div className="relative">
              {/* Sliding active indicator */}
              <div
                className="absolute left-0 w-1 bg-[#090909] transition-all duration-300 ease-in-out"
                style={{ top: indicatorStyle.top, height: indicatorStyle.height }}
              />
              {/* Background track */}
              <div className="absolute left-0 top-0 h-full w-1 bg-[#F3EDE9]" />
              {sidebarSections.map((section, index) => (
                <div
                  key={section.value}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  onClick={() => handleClick(index)}
                  className="flex gap-2 items-center py-5 pl-5 cursor-pointer"
                >
                  <div>{section.icon}</div>
                  <span className={`text-17 font-semibold whitespace-nowrap transition-colors duration-300 ${index === activeSection ? 'text-[#090909]' : 'text-[#6b6b6b]'}`}>
                    {section.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SidebarPDP;